// Pure color derivation utilities for accent color feature
// Zero dependencies -- all math is inline

export interface AccentColorSet {
  primary: string;
  primaryHover: string;
  primaryLight: string;
  secondary: string;
  secondaryHover: string;
}

/** Parse #rrggbb to [h (0-360), s (0-100), l (0-100)] */
export function hexToHsl(hex: string): [number, number, number] {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;

  if (max === min) {
    return [0, 0, Math.round(l * 100)];
  }

  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

  let h: number;
  if (max === r) {
    h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
  } else if (max === g) {
    h = ((b - r) / d + 2) / 6;
  } else {
    h = ((r - g) / d + 4) / 6;
  }

  return [Math.round(h * 360), Math.round(s * 100), Math.round(l * 100)];
}

/** Convert HSL back to #rrggbb */
export function hslToHex(h: number, s: number, l: number): string {
  const sNorm = s / 100;
  const lNorm = l / 100;

  const c = (1 - Math.abs(2 * lNorm - 1)) * sNorm;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = lNorm - c / 2;

  let r: number, g: number, b: number;

  if (h < 60) {
    [r, g, b] = [c, x, 0];
  } else if (h < 120) {
    [r, g, b] = [x, c, 0];
  } else if (h < 180) {
    [r, g, b] = [0, c, x];
  } else if (h < 240) {
    [r, g, b] = [0, x, c];
  } else if (h < 300) {
    [r, g, b] = [x, 0, c];
  } else {
    [r, g, b] = [c, 0, x];
  }

  const toHex = (n: number) =>
    Math.round((n + m) * 255)
      .toString(16)
      .padStart(2, "0");

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

/**
 * How far `derivePrimaryHover` moves a fill, in HSL lightness points. The
 * DIRECTION is derived per accent; only the distance is fixed.
 */
export const HOVER_LIGHTNESS_STEP = 10;

/**
 * The hover fill for a surface filled with `hex`: the same color moved
 * `HOVER_LIGHTNESS_STEP` lightness points AWAY from the luminance of the ink
 * that `onAccentInk` draws on it — darker under a light ink, lighter under a
 * dark one.
 *
 * The rule is SEPARATION from the ink, not darkness. Those two coincide only
 * while the ink is the lighter half of the pair, which is why "hover darkens" is
 * the shape a hover derivation usually takes. Once the ink is the darker half,
 * darkening the fill closes the gap the rest state opened: the near-black ink
 * that reads 5.40:1 on the orange preset at rest reads 3.61:1 on a fill
 * darkened 10 points from it, under `AA_NORMAL_TEXT`.
 *
 * Choosing a better ink cannot rescue a fixed direction, because one button
 * carries ONE ink through both states — swapping ink mid-interaction reads as a
 * different button, not as the same button hovered. On the orange preset that
 * single ink would have to sit at or below 0.0156 relative luminance to clear
 * the floor on the rest fill and at or above 0.8373 to clear it on a darkened
 * hover fill. No color satisfies both, and the same squeeze exists for every
 * accent whose ink is dark.
 *
 * Moving the FILL instead makes the hover pairing at least as strong as the rest
 * pairing for every accent: with the ink fixed, contrast is monotone in the
 * fill's luminance, and at a fixed hue and saturation luminance is monotone in
 * HSL lightness. So the floor `onAccentInk` establishes at rest carries into
 * hover by construction rather than by coincidence.
 *
 * The accepted cost: on an accent dark enough to take dark ink, hover LIGHTENS,
 * inverting the convention that a button darkens under the cursor. Legibility
 * outranks the convention.
 */
export function derivePrimaryHover(hex: string): string {
  const [h, s, l] = hexToHsl(hex);
  const inkIsLighterThanFill = relativeLuminance(onAccentInk(hex)) > relativeLuminance(hex);
  return inkIsLighterThanFill
    ? hslToHex(h, s, Math.max(0, l - HOVER_LIGHTNESS_STEP))
    : hslToHex(h, s, Math.min(100, l + HOVER_LIGHTNESS_STEP));
}

/** Light mode: set l=95; dark mode: set l=20 */
export function derivePrimaryLight(hex: string, mode: "light" | "dark"): string {
  const [h, s] = hexToHsl(hex);
  return hslToHex(h, s, mode === "light" ? 95 : 20);
}

/** Hue + 150 degrees (mod 360), saturation clamped to 35-75 range */
export function deriveSecondary(hex: string): string {
  const [h, s, l] = hexToHsl(hex);
  const newH = (h + 150) % 360;
  const newS = Math.min(75, Math.max(35, s));
  return hslToHex(newH, newS, l);
}

/** Apply hover derivation (lightness - 10) to the secondary color */
export function deriveSecondaryHover(hex: string): string {
  const secondary = deriveSecondary(hex);
  return derivePrimaryHover(secondary);
}

/** Split #rrggbb into its three 0-255 channels, or undefined if not 6-digit hex */
function hexChannels(hex: string): [number, number, number] | undefined {
  if (!/^#[0-9a-fA-F]{6}$/.test(hex)) return undefined;
  return [
    parseInt(hex.slice(1, 3), 16),
    parseInt(hex.slice(3, 5), 16),
    parseInt(hex.slice(5, 7), 16),
  ];
}

/**
 * Straight sRGB interpolation between two #rrggbb colors: `weight` of `hex`
 * plus `1 - weight` of `other`, per channel on the 0-255 values, rounded to
 * the nearest integer. This is the exact arithmetic of CSS
 * `color-mix(in srgb, hex <weight>%, other)`, so a color mixed here matches the
 * same mix a browser computes. Web does NOT import this — it declares
 * `color-mix()` in the stylesheet and lets the browser evaluate it. The
 * consumers are the mobile form-theme layer, which has no `color-mix`, and the
 * reconciliation test that evaluates the web stylesheet's own expressions to
 * compare the two; matching browser arithmetic is what makes that comparison
 * meaningful. `weight` is clamped to 0-1. A non-6-digit-hex input on either
 * side returns `hex` unchanged — mixing is a best-effort tint, never a crash,
 * and the callers that mix against a theme table assert their bases are hex.
 */
export function mixHex(hex: string, other: string, weight: number): string {
  const a = hexChannels(hex);
  const b = hexChannels(other);
  if (a === undefined || b === undefined) return hex;
  const [ar, ag, ab] = a;
  const [br, bg, bb] = b;
  const w = Math.min(1, Math.max(0, weight));
  const blend = (x: number, y: number) =>
    Math.round(w * x + (1 - w) * y)
      .toString(16)
      .padStart(2, "0");
  return `#${blend(ar, br)}${blend(ag, bg)}${blend(ab, bb)}`;
}

/** WCAG 2.1 relative luminance from sRGB hex */
export function relativeLuminance(hex: string): number {
  const linearize = (c: number) => {
    const srgb = c / 255;
    return srgb <= 0.04045 ? srgb / 12.92 : Math.pow((srgb + 0.055) / 1.055, 2.4);
  };

  const r = linearize(parseInt(hex.slice(1, 3), 16));
  const g = linearize(parseInt(hex.slice(3, 5), 16));
  const b = linearize(parseInt(hex.slice(5, 7), 16));

  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** WCAG contrast ratio: (lighter + 0.05) / (darker + 0.05) */
export function contrastRatio(hex1: string, hex2: string): number {
  const l1 = relativeLuminance(hex1);
  const l2 = relativeLuminance(hex2);
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);
  return (lighter + 0.05) / (darker + 0.05);
}

/**
 * The WCAG 2.1 contrast floor for normal-size text (1.4.3 Contrast Minimum).
 * The single source of truth for the number: the derivation below targets it and
 * every suite that measures a shipped pairing asserts against this same export,
 * so a test can no longer hold the code to a floor the code is not aiming for.
 */
export const AA_NORMAL_TEXT = 4.5;

/**
 * The closed set of inks that may be drawn on an accent-filled surface, keyed by
 * the role each one plays in `onAccentInk`.
 *
 * `preferred` is the near-black the dark mockups draw on their own accent-filled
 * surfaces (the monogram tile, the current-section rail dot, the primary
 * button). `lightest` and `darkest` are the two extremes of the ink range, and
 * between them they can reach a higher worst-case ratio than any other pair.
 */
export const ON_ACCENT_INK = {
  preferred: "#04110e",
  lightest: "#ffffff",
  darkest: "#000000",
} as const;

/**
 * How far ABOVE the floor `preferred` must sit before it is chosen over the
 * lightest/darkest pair, in contrast-ratio points.
 *
 * It exists because the ratio this module computes is not the ratio a browser
 * paints. A dark-mode accent is `mixHex(hex, "#ffffff", 0.78)`, which rounds each
 * channel to 8 bits, while CSS evaluates `color-mix(in srgb, … 78%, #ffffff)` in
 * float. Measured over a step-3 walk of the whole hex space, that divergence
 * moves the ratio by as much as 0.076 — so a gate that admitted `preferred` at
 * exactly 4.5 could ship a RENDERED pairing at 4.4725, genuinely under the
 * floor. The same margin absorbs an auditor who rounds luminance or the ratio
 * itself before comparing.
 *
 * 0.1 is not arbitrary: it is the smallest round value at or above 0.0826, the
 * point where `AA_NORMAL_TEXT + margin` exceeds the lightest/darkest pair's own
 * crossover of 4.5826. Above that the gate is no longer the binding constraint
 * on the floor, so the derivation reaches the highest floor any two-candidate
 * scheme can reach and a larger margin buys nothing. It must also stay below
 * 0.303, the headroom of the tightest preset pairing that legitimately keeps the
 * authored ink (indigo's dark accent at 4.8033) — above that, presets start
 * losing the mockups' value for no gain.
 */
export const ON_ACCENT_INK_MARGIN = 0.1;

/**
 * The ink to draw ON TOP of a surface filled with `accentHex`, chosen so the
 * pairing clears the WCAG AA normal-text floor for EVERY accent — including the
 * arbitrary 6-digit hexes `validateAccentColor` accepts, not just the eight
 * presets.
 *
 * No fixed on-accent ink can be correct, which is why this is derived rather
 * than declared. `#ffffff` reads 3.56:1 on the shipped orange preset
 * (`#ea580c`), 3.74:1 on teal and 3.77:1 on emerald; the near-black
 * `ON_ACCENT_INK.preferred` reads 4.42:1 on the slate preset's dark-mode accent
 * (`#6f7a8a`). All four are below 4.5:1.
 *
 * Three candidates in preference order rather than two:
 *
 *   1. `preferred` wherever it clears the floor by `ON_ACCENT_INK_MARGIN`, so the
 *      value the mockups draw survives everywhere it is legible (it holds for six
 *      of the eight presets' dark-mode accents, for three of their raw light-mode
 *      accents, and for the fixed reference teal the print stylesheet uses).
 *   2. `lightest` for an accent too dark for (1).
 *   3. `darkest` for the narrow accent-luminance band where NEITHER clears.
 *
 * (3) is load-bearing. Taking whichever of `#ffffff` / `#04110e` contrasts
 * better does NOT guarantee AA: at the accent luminance where the two are
 * equally bad, the better of them is still only 4.386:1, so a band of accents
 * fails both ways. `#000000` has the lowest relative luminance an ink can have,
 * so the same crossover for a black-and-white pair bottoms out at 4.583:1 — the
 * highest floor any two candidates can reach, and it covers the band with room
 * over the floor.
 *
 * A `accentHex` that is not a 6-digit hex makes every comparison NaN and yields
 * `darkest`, so a malformed stored accent renders legible ink on the light
 * surface it will otherwise fall back to rather than no ink at all.
 */
export function onAccentInk(accentHex: string): string {
  if (contrastRatio(ON_ACCENT_INK.preferred, accentHex) >= AA_NORMAL_TEXT + ON_ACCENT_INK_MARGIN) {
    return ON_ACCENT_INK.preferred;
  }
  return contrastRatio(ON_ACCENT_INK.lightest, accentHex) >=
    contrastRatio(ON_ACCENT_INK.darkest, accentHex)
    ? ON_ACCENT_INK.lightest
    : ON_ACCENT_INK.darkest;
}

/**
 * The weight the form-theme stylesheet's dark blocks lighten the org accent by
 * before any accent-derived slot reads it:
 * `--form-accent: color-mix(in srgb, var(--color-primary) 78%, #ffffff)`. The
 * ink that has to sit on a DARK-mode accent fill is therefore derived from the
 * lightened accent, not from the raw one — deriving it from the raw accent would
 * answer the question for a fill the dark document never paints. Pinned against
 * the stylesheet's own percentage by test, so the two cannot drift.
 */
export const DARK_FORM_ACCENT_WHITE_MIX = 0.78;

/**
 * Both on-accent inks for one org accent, one per mode.
 *
 * BOTH are published as their own custom property, and neither is
 * mode-dependent, because the stylesheet's light and dark theme blocks coexist
 * in the same sheet: a single `--color-accent-ink` would make each block's
 * correctness depend on which mode the accent hook last ran for, and the dark
 * block would inherit a light-mode ink on every theme it did not re-declare.
 * Each block references the property for its own mode instead, and the mode
 * the browser is actually rendering picks the block.
 */
export function accentInkVars(hex: string): { light: string; dark: string } {
  return {
    light: onAccentInk(hex),
    dark: onAccentInk(mixHex(hex, "#ffffff", DARK_FORM_ACCENT_WHITE_MIX)),
  };
}

/** Returns true if contrast ratio >= 3:1 against #ffffff and >= 2:1 against #0f172a */
export function validateAccentColor(hex: string): boolean {
  return contrastRatio(hex, "#ffffff") >= 3 && contrastRatio(hex, "#0f172a") >= 2;
}

/** 8 preset accent colors */
export const ACCENT_PRESETS: Record<string, string> = {
  blue: "#2563eb",
  indigo: "#4f46e5",
  violet: "#7c3aed",
  rose: "#e11d48",
  orange: "#ea580c",
  emerald: "#059669",
  teal: "#0d9488",
  slate: "#475569",
};

/** Resolve a preset slug or hex string to a hex value; fallback #2563eb */
export function resolveAccentHex(value: string): string {
  if (value.startsWith("#")) {
    return value;
  }
  return ACCENT_PRESETS[value] ?? "#2563eb";
}

/** Derive a full accent color set from a hex value and theme mode */
export function deriveAccentColors(hex: string, mode: "light" | "dark"): AccentColorSet {
  return {
    primary: hex,
    primaryHover: derivePrimaryHover(hex),
    primaryLight: derivePrimaryLight(hex, mode),
    secondary: deriveSecondary(hex),
    secondaryHover: deriveSecondaryHover(hex),
  };
}
